import { Link } from "react-router-dom";
import { ArrowRight, Mail, Phone, MapPin, Clock, CheckCircle2 } from "lucide-react";
import { SITE, IMG } from "@/lib/site";
import Chapter from "@/components/cinema/Chapter";
import { Reveal, LineReveal } from "@/components/cinema/Reveal";
import { positions, perks } from "@/lib/careersContent";

const APPLY_SUBJECT = encodeURIComponent("Career application — BackHome Restoration Inc.");

function PositionCard({ p, i }) {
  return (
    <Reveal delay={0.1 + i * 0.06}>
      <article className="glass-dark p-8 md:p-10 grid md:grid-cols-12 gap-8 hover:bg-[hsl(var(--gaza-teal-deep))]/80 transition" data-testid={`position-card-${p.id}`}>
        <div className="md:col-span-4">
          <div className="flex items-center gap-3 mb-5">
            <span className="w-11 h-11 grid place-items-center bg-[hsl(var(--gaza-blue-bright))]"><p.icon className="w-5 h-5 text-white" strokeWidth={1.7} /></span>
            <span className="text-[10px] tracking-[0.28em] uppercase font-extrabold text-white/55">0{i + 1}</span>
          </div>
          <h3 className="display-serif text-2xl md:text-3xl leading-tight">{p.title}</h3>
          <p className="text-white/80 mt-3 leading-relaxed">{p.summary}</p>
          <div className="mt-5 space-y-2 text-sm text-white/65">
            <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-[hsl(var(--gaza-blue-bright))]" strokeWidth={1.7} /><span>{p.type}</span></div>
            <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[hsl(var(--gaza-blue-bright))]" strokeWidth={1.7} /><span>{p.location}</span></div>
          </div>
        </div>
        <div className="md:col-span-5 md:border-l md:border-white/10 md:pl-8">
          <div className="text-[11px] tracking-[0.28em] uppercase font-extrabold text-white/70 mb-4">What you'll do</div>
          <ul className="space-y-3">
            {p.responsibilities.map((r) => (
              <li key={r} className="flex items-start gap-3 text-white/85 leading-relaxed">
                <CheckCircle2 className="w-4 h-4 text-[hsl(var(--gaza-blue-bright))] mt-1 flex-shrink-0" strokeWidth={2} />
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="md:col-span-3 md:border-l md:border-white/10 md:pl-8 flex flex-col">
          <div className="text-[11px] tracking-[0.28em] uppercase font-extrabold text-white/70 mb-4">What we're looking for</div>
          <ul className="space-y-3 flex-1">
            {p.requirements.map((r) => <li key={r} className="text-sm text-white/80 leading-relaxed">· {r}</li>)}
          </ul>
          <a href={`${SITE.hrEmailHref}?subject=${encodeURIComponent(`Application — ${p.title}`)}`} className="btn-gold mt-6 justify-center" data-testid={`apply-btn-${p.id}`}>
            Apply <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </article>
    </Reveal>
  );
}

export default function Careers() {
  return (
    <div data-testid="page-careers" className="bg-[hsl(var(--gaza-teal-deep))] text-white">
      <Chapter img={IMG.crewTruck} overlay="left" testId="careers-header">
        <div className="max-w-4xl">
          <Reveal y={16}><div className="eyebrow eyebrow-light mb-6">Careers at BackHome</div></Reveal>
          <LineReveal as="h1" className="display-serif text-4xl sm:text-5xl md:text-6xl leading-[0.95]" lines={["Build a career on", "the days people", "need us most."]} />
          <Reveal delay={0.5}>
            <p className="text-white/80 text-lg mt-8 leading-relaxed max-w-2xl">
              We're hiring across every lane of the recovery: emergency response, contents, pack-out, logistics, project management and compliance. If you take pride in showing up prepared and treating a client's home like your family's, we want to meet you.
            </p>
          </Reveal>
          <Reveal delay={0.65}>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <a href={`${SITE.hrEmailHref}?subject=${APPLY_SUBJECT}`} className="btn-gold" data-testid="careers-hero-apply"><Mail className="w-4 h-4" /> Apply — {SITE.hrEmail}</a>
              <a href="#open-positions" className="btn-outline" data-testid="careers-hero-view">View open positions <ArrowRight className="w-4 h-4" /></a>
            </div>
          </Reveal>
        </div>
      </Chapter>

      <Chapter img={IMG.warehouseContents} overlay="dark" testId="careers-why">
        <div className="grid md:grid-cols-12 gap-10 items-end mb-16">
          <div className="md:col-span-5">
            <Reveal y={16}><div className="eyebrow eyebrow-light mb-5">Why BackHome</div></Reveal>
            <LineReveal className="display-serif text-4xl md:text-5xl leading-[0.98]" lines={["A serious company,", "run by people", "who still care."]} />
          </div>
          <Reveal delay={0.4} className="md:col-span-6 md:col-start-7 text-white/80 leading-relaxed text-lg">
            We're growing across the GTA because clients keep coming back. That only happens when the crew is proud of the work. Here's what that looks like day to day.
          </Reveal>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {perks.map((p, i) => (
            <Reveal key={p.title} delay={0.15 + i * 0.1} className="h-full">
              <article className="glass-dark h-full p-8 md:p-10 group hover:bg-[hsl(var(--gaza-teal-deep))]/80 transition" data-testid={`perk-card-${i + 1}`}>
                <span className="w-11 h-11 grid place-items-center bg-[hsl(var(--gaza-blue-bright))] mb-6"><p.icon className="w-5 h-5 text-white" strokeWidth={1.7} /></span>
                <h3 className="display-serif text-xl leading-tight">{p.title}</h3>
                <p className="text-white/75 mt-3 leading-relaxed text-base">{p.desc}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </Chapter>

      <Chapter img={IMG.iceStorm} overlay="dark" testId="careers-positions">
        <div id="open-positions" className="scroll-mt-28" />
        <div className="grid md:grid-cols-12 gap-10 items-end mb-16">
          <div className="md:col-span-6">
            <Reveal y={16}><div className="eyebrow eyebrow-light mb-5">Open positions</div></Reveal>
            <LineReveal className="display-serif text-4xl md:text-5xl leading-[0.98]" lines={["We're hiring across", "every lane of", "the recovery."]} />
          </div>
          <Reveal delay={0.4} className="md:col-span-5 md:col-start-8 text-white/80 leading-relaxed text-lg">
            Send your résumé and a short note about the role that fits you to{" "}
            <a href={SITE.hrEmailHref} className="font-semibold text-white underline decoration-[hsl(var(--gaza-blue-bright))] underline-offset-4" data-testid="careers-inline-email">{SITE.hrEmail}</a>
            . Include the position title in the subject line.
          </Reveal>
        </div>
        <div className="space-y-5">
          {positions.map((p, i) => <PositionCard key={p.id} p={p} i={i} />)}
        </div>
      </Chapter>

      <Chapter img={IMG.contentsPackout} overlay="left" testId="careers-how-to-apply">
        <div className="grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6">
            <Reveal y={16}><div className="eyebrow eyebrow-light mb-5">How to apply</div></Reveal>
            <LineReveal className="display-serif text-4xl md:text-5xl leading-[0.98]" lines={["One email.", "One conversation.", "A clear next step."]} />
            <Reveal delay={0.45}>
              <p className="text-white/80 mt-6 leading-relaxed text-lg max-w-xl">
                Send us your résumé and a short note about the role that fits you. We read every application and reply within a few business days.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.3} className="md:col-span-6">
            <div className="glass-dark p-8 md:p-10">
              <div className="text-[11px] tracking-[0.28em] uppercase text-[hsl(var(--gaza-blue-bright))] font-extrabold mb-6">Email HR directly</div>
              <a href={SITE.hrEmailHref} className="inline-flex items-center gap-4 group" data-testid="careers-hr-email-cta">
                <span className="w-12 h-12 grid place-items-center bg-[hsl(var(--gaza-blue-bright))] group-hover:bg-white transition">
                  <Mail className="w-5 h-5 text-white group-hover:text-[hsl(var(--gaza-blue-bright))]" />
                </span>
                <span>
                  <div className="text-[10px] tracking-[0.28em] uppercase text-white/60 font-extrabold">HR &amp; Recruitment</div>
                  <div className="display-chunky text-xl md:text-2xl text-white group-hover:text-[hsl(var(--gaza-blue-bright))] transition break-all">{SITE.hrEmail}</div>
                </span>
              </a>
              <div className="mt-8 space-y-3 text-sm text-white/80 leading-relaxed">
                <div>1. Attach your résumé (PDF preferred).</div>
                <div>2. Put the position title in the subject line.</div>
                <div>3. In 3–4 lines, tell us why this role fits you.</div>
              </div>
              <div className="mt-8 pt-6 border-t border-white/10 text-sm text-white/75">
                Prefer to talk? Call our line 24/7 at{" "}
                <a href={SITE.phoneHref} className="font-semibold text-white hover:text-[hsl(var(--gaza-blue-bright))]" data-testid="careers-phone-inline">{SITE.phone}</a>{" "}
                and ask for HR.
              </div>
            </div>
          </Reveal>
        </div>
      </Chapter>

      <Chapter img={IMG.stageBackHome} overlay="center" className="chapter-compact" testId="careers-cta">
        <div className="text-center flex flex-col items-center">
          <LineReveal className="display-serif text-3xl md:text-5xl max-w-3xl leading-[0.98]" lines={["Don't see your role?", "Introduce yourself anyway."]} />
          <Reveal delay={0.3}>
            <p className="text-white/80 mt-5 max-w-xl mx-auto leading-relaxed">
              We're always interested in meeting people who take pride in this work. Send us a note and we'll keep you in mind as we grow.
            </p>
          </Reveal>
          <Reveal delay={0.45}>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <a href={SITE.hrEmailHref} className="btn-gold" data-testid="careers-general-email"><Mail className="w-4 h-4" /> {SITE.hrEmail}</a>
              <Link to="/contact" className="btn-outline" data-testid="careers-contact-btn">Contact Us <ArrowRight className="w-4 h-4" /></Link>
              <a href={SITE.phoneHref} className="btn-outline" data-testid="careers-phone-btn"><Phone className="w-4 h-4" /> {SITE.phone}</a>
            </div>
          </Reveal>
        </div>
      </Chapter>
    </div>
  );
}
